import { Component } from "react";
import { Provider, connect } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { createRoot } from "react-dom/client";

import { createTheme, ThemeProvider } from "@mui/material/styles";

import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faCalendarAlt, faPersonBooth, faAddressBook, faHome, faChartLine, faTrash,
  faSignOutAlt, faEdit, faSave, faPlus, faTimesCircle, faSearch, faTrashRestore,
  faTools, faUsers, faLanguage, faHistory, faFile, faFileExcel, faFileWord, faFileCsv,
  faFileImage, faFilePdf, faFilePowerpoint, faFileVideo, faFileAudio, faPrint, faListAlt,
  faFlag, faBabyCarriage, faIdCard, faMapMarkedAlt, faRainbow, faUserCheck, faEuroSign, faCar,
  faPaste, faBook, faUsersSlash, faRing, faBaby, faUserCog, faHeartBroken, faQuestionCircle
} from "@fortawesome/free-solid-svg-icons";

import LoadingOverlay from "@auto/widgets/LoadingOverlay";
import { AlertDialog } from "@auto/widgets";
import { OdeevBrowser } from "@auto/browsers/OdeevBrowser";
import { AppNavigator } from "@auto/widgets/AppNavigator";
import PrintService from "@auto/services/PrintService";
import { HttpErrorDialog } from "@auto/widgets/HttpErrorDialog";
import { store } from "@auto/store";
import { GUI_LANGUAGE_CHANGE_EVENT, PRINT_EVENT } from "@auto/events/Gui";
import { metadataLoader } from "@auto/metadata/MetadataLoader";
import { t, setLocale, getLocale } from "@auto/services";

import { setUserInfoFromSession } from "@auto/users/UserInfo";
import { QalaaPkceClient } from "@auto/services/qalaa-pkce-client.mjs";



import "bootstrap/dist/css/bootstrap.css";

library.add(
  faCalendarAlt, faPersonBooth, faAddressBook, faHome, faChartLine, faTrash,
  faSignOutAlt, faEdit, faSave, faPlus, faTimesCircle, faSearch, faTrashRestore,
  faTools, faUsers, faLanguage, faHistory, faFile, faFileExcel, faFileWord, faFileCsv,
  faFileImage, faFilePdf, faFilePowerpoint, faFileVideo, faFileAudio, faPrint, faListAlt,
  faFlag, faBabyCarriage, faIdCard, faMapMarkedAlt, faRainbow, faUserCheck, faEuroSign, faCar,
  faPaste, faBook, faUsersSlash, faRing, faBaby, faUserCog, faHeartBroken, faQuestionCircle
);



const loc = getLocale();
const theme = createTheme({
  direction: (loc.startsWith("ar") && loc !== "ar-DA")? "rtl" : "ltr",
});

const languages = {
  "en-US": "en-US",
  "fr-FR": "fr-FR",
  "ht-HT": "ht-HT"
};

const auth = new QalaaPkceClient({
  baseUrl: window.location.origin,
  loginUrl: "/api/oauth2/authorization/qalaa",
  sessionUrl: "/api/__session/me",
  logoutUrl: "/api/logout",
  postLogoutRedirectUri: window.location.origin,
  onError: (err) => {
    console.error("Qalaa session error", err);
  },
});

class App extends Component {
  constructor(props) {
    super(props);

	this.state = {
	  ready: {
	    auth: false,
	    userInfo: false,
	  },
	  session: null,
	  language: getLocale(),
	  loadingMessage: t("Loading metadata")
	};

	GUI_LANGUAGE_CHANGE_EVENT.subscribe((lang) => {
	  setLocale(lang);
	  this.setState({ language: lang });
	});
  }

  componentDidMount() {
    this.bootstrap();
  }

  bootstrap = async () => {
    try {
		const session = await auth.resume();
		
		if (!session?.authenticated) {
		  await auth.authenticate({
		    mode: "redirect",
		    returnTo: window.location.href,
		  });
		  return;
		}
		
		this.setState({ session });
		this.setReady("auth");
		
		setUserInfoFromSession(session);
		this.setReady("userInfo");

		const lang = localStorage.getItem("locale") || "ht-HT";
		setLocale(lang);
		this.setState({ language: lang });
    } catch (err) {
      console.error("Application bootstrap failed", err);
      this.setState({ loadingMessage: t("Application bootstrap failed") });
    }
  };

  setReady = (stateName) => {
    this.setState((prev) => ({
      ready: {
        ...prev.ready,
        [stateName]: true,
      },
      loadingMessage: `${stateName} is ready`,
    }));
  };

	isReady = () => {
	  return Object.values(this.state.ready).every(Boolean);
	};

  render() {
    const ready = this.isReady();

    if (this.props.failedConnection) {
      return (
        <AlertDialog
          confirmMessage={t("Communication failed with the server, please check your internet connection and refresh the page")}
          open={true}
          noCancel={true}
          noAgree={true}
        />
      );
    }

    return (
      <LoadingOverlay active={!ready} spinner text={this.state.loadingMessage}>
        <PrintService trigger={PRINT_EVENT} />
        {ready && (
          <BrowserRouter>
              <OdeevBrowser languages={languages} navigator={AppNavigator} session={this.state.session}/>
          </BrowserRouter>
        )}
      </LoadingOverlay>
    );
  }
}

const mapStateToProps = (state) => ({
  failedConnection: !!state.failedConnection,
});

const AppContainer = connect(mapStateToProps)(App);

export default function AppRoot() {
  return (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
		<>
			<AppContainer />
			<HttpErrorDialog />
		</>
      </Provider>
    </ThemeProvider>
  );
}

createRoot(document.getElementById("root")).render(<AppRoot />);